import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { DocumentTitle } from 'components/DocumentTitle';
import { BaseLandingPage, MainContent, TopRow } from 'components/page/BaseLandingPage';
import { useCorporationIssueMetrics } from 'custom_hooks/data_hooks/corporation/useCorporationIssueMetrics';
import {
    CorporationAssetsWithAlertsWidget,
    CorporationAssetsWithAlertsWidgetRef,
} from 'pages/Corporation/assets_with_alerts/CorporationAssetsWithAlertsWidget';
import { CorporationOverviewFilterPane } from 'pages/Corporation/CorporationOverviewFilterPane';
import {
    CorporationHardwareStatusWidget,
    CorporationHardwareStatusWidgetRef,
} from 'pages/Corporation/hardware_status/CorporationHardwareStatusWidget';
import {
    CorporationIssuesWidget,
    CorporationIssuesWidgetRef,
} from 'pages/Corporation/issues/CorporationIssuesWidget';
import { CorporationLeftColumn, CorporationRightColumn } from 'pages/Corporation/styled';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IssueMetrics } from 'shared/issue_metrics/IssueMetricsWidget';
import { LastUpdated } from 'shared/last_updated/LastUpdated';
import { LandingPageFilterAndLastUpdated } from 'shared/last_updated/styled';

type WidgetName = 'IssueMetrics' | 'Issues' | 'HardwareStatus' | 'AssetsWithAlerts';

export function CorporationLandingPage({
    corporation,
}: {
    corporation: ICorporationResponse;
}) {
    const [filters, setFilters] = useState<CorporationOverviewFilters>({
        favoritesFilter: undefined,
    });

    const {
        data: metrics,
        isLoading,
        isFetching,
        error,
        refetch: refetchMetrics,
    } = useCorporationIssueMetrics(corporation.Id, filters);

    const [loadingWidgets, setLoadingWidgets] = useState<Record<WidgetName, boolean>>({
        IssueMetrics: false,
        Issues: false,
        HardwareStatus: false,
        AssetsWithAlerts: false,
    });

    const issuesWidgetRef = useRef<CorporationIssuesWidgetRef>(null);
    const hardwareStatusWidgetRef = useRef<CorporationHardwareStatusWidgetRef>(null);
    const assetsWithAlertsWidgetRef = useRef<CorporationAssetsWithAlertsWidgetRef>(null);

    const handleRefetch = useCallback(() => {
        refetchMetrics();
        setLoadingWidgets((prev) => ({
            ...prev,
            IssueMetrics: true,
            Issues: true,
            HardwareStatus: true,
            AssetsWithAlerts: true,
        }));

        const refetchPromises = [
            issuesWidgetRef.current?.refetch(),
            hardwareStatusWidgetRef.current?.refetch(),
            assetsWithAlertsWidgetRef.current?.refetch(),
        ].filter(Boolean);

        Promise.all(refetchPromises).finally(() =>
            setLoadingWidgets((prev) => ({
                ...prev,
                IssueMetrics: false,
                Issues: false,
                HardwareStatus: false,
                AssetsWithAlerts: false,
            }))
        );
    }, [refetchMetrics]);

    const pageTitle = useMemo(() => `${corporation.Name} Overview`, [corporation.Name]);

    const isAnyWidgetLoading = Object.values(loadingWidgets).some(Boolean);

    return (
        <>
            <DocumentTitle title={pageTitle} />
            <BaseLandingPage>
                <TopRow>
                    <LandingPageFilterAndLastUpdated>
                        <CorporationOverviewFilterPane
                            corporationId={corporation.Id}
                            filters={filters}
                            onChange={setFilters}
                        />
                        <LastUpdated
                            isLoading={isFetching || isAnyWidgetLoading}
                            reload={handleRefetch}
                        />
                    </LandingPageFilterAndLastUpdated>

                    <IssueMetrics
                        corporationId={corporation.Id}
                        metrics={metrics ?? null}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        error={error instanceof Error ? error : null}
                    />
                </TopRow>

                <MainContent>
                    <CorporationLeftColumn>
                        <CorporationIssuesWidget
                            corporation={corporation}
                            filters={filters}
                            ref={issuesWidgetRef}
                        />
                        <CorporationHardwareStatusWidget
                            corporation={corporation}
                            filters={filters}
                            ref={hardwareStatusWidgetRef}
                        />
                    </CorporationLeftColumn>

                    <CorporationRightColumn>
                        <CorporationAssetsWithAlertsWidget
                            corporation={corporation}
                            filters={filters}
                            ref={assetsWithAlertsWidgetRef}
                        />
                    </CorporationRightColumn>
                </MainContent>
            </BaseLandingPage>
        </>
    );
}
